import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { MapPin, Search, ArrowRight, Store, ChevronLeft, ChevronRight, Truck } from "lucide-react";

const FALLBACK = [{
  title: "Your neighbourhood. Your shops. Your everyday essentials.",
  subtitle: "Order from the shops around you and get your everyday needs delivered to your doorstep.",
  image: "/hero-grofkart.jpg", cta_text: "Explore Nearby Shops", cta_url: "/stores",
}];

export const HeroSlider = ({ onOpenLocation }) => {
  const nav = useNavigate();
  const { location } = useLocation();
  const [q, setQ] = useState("");
  const [i, setI] = useState(0);

  const { data } = useQuery({ queryKey: ["banners"], queryFn: () => api.get("/banners").then((r) => r.data) });
  const slides = data && data.length ? data : FALLBACK;

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setI((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [slides.length]);

  const s = slides[i % slides.length];
  const go = (d) => setI((p) => (p + d + slides.length) % slides.length);
  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <section data-testid="hero-section" className="relative mb-8 overflow-hidden rounded-[28px] bg-brand-deep">
      <div className="absolute inset-0">
        <img src={s.image} alt="" className="h-full w-full object-cover object-right transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-deep via-brand-deep/90 to-brand-deep/30" />
      </div>

      <div className="relative z-10 px-6 py-10 sm:px-10 sm:py-14 lg:py-16">
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-surge px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
            <Truck className="h-3.5 w-3.5" /> Fast Local Delivery · From Neighbourhood Stores
          </span>
          <h1 data-testid="hero-headline" className="mt-4 font-head text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
            {s.title}
          </h1>
          <p data-testid="hero-subheadline" className="mt-3 max-w-md text-sm text-emerald-50/90 sm:text-base">{s.subtitle}</p>

          <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-white/95 p-2 shadow-xl backdrop-blur sm:flex-row sm:items-center">
            <button data-testid="hero-location-picker-btn" onClick={onOpenLocation}
              className="flex min-w-0 items-center gap-2 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-100 sm:max-w-[38%]">
              <MapPin className="h-4 w-4 shrink-0 text-brand" />
              <span className="truncate text-sm font-semibold text-slate-800">
                {location?.city || location?.address || "Set your location"}
              </span>
            </button>
            <form onSubmit={submit} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search groceries, products or stores"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-brand focus:bg-white" />
            </form>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button data-testid="hero-shop-now-cta" onClick={() => nav(s.cta_url || "/stores")}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-surge px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.03] active:scale-95">
              {s.cta_text || "Shop Now"} <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-partner-cta" onClick={() => nav("/partner")}
              className="inline-flex items-center gap-2 rounded-xl border border-white/40 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/20">
              <Store className="h-4 w-4" /> Become a Grofkart Store
            </button>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <>
          <button data-testid="hero-slider-prev-btn" onClick={() => go(-1)} aria-label="Previous"
            className="absolute left-3 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur hover:bg-white/30 md:flex">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button data-testid="hero-slider-next-btn" onClick={() => go(1)} aria-label="Next"
            className="absolute right-3 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur hover:bg-white/30 md:flex">
            <ChevronRight className="h-5 w-5" />
          </button>
          <div data-testid="hero-slider-container" className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-1.5">
            {slides.map((_, idx) => (
              <button key={idx} data-testid="hero-slider-dot-indicator" onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i % slides.length ? "w-6 bg-white" : "w-1.5 bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
};
